// Browser-side client for the public, read-only tournament view (the QR share
// link). Unlike `apiClient`, it never sends a bearer token: the visitor need
// not be logged in. It hits the single public endpoint
// `GET /api/public/tournaments/:id` and mirrors `apiClient`'s base-URL rule
// (`/api` by default, overridable with `VITE_API_BASE_URL`).

import type {
  CompanyRegistrationView,
  Location,
  PublicAthleteProfile,
  PublicClubHome,
  PublicPhoto,
  RegistrationInput,
} from '../domain';
import type { TournamentState } from '../domain';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

interface TournamentResponse {
  tournament: TournamentState | null;
}

/**
 * Load a shared tournament snapshot by id. Resolves to null when the tournament
 * does not exist (HTTP 404 — e.g. a stale or mistyped link). Throws on network
 * or unexpected server errors so the caller can show a retry state.
 */
export async function fetchPublicTournament(
  id: string,
): Promise<TournamentState | null> {
  const res = await fetch(
    `${BASE_URL}/public/tournaments/${encodeURIComponent(id)}`,
    { headers: { Accept: 'application/json' } },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      // non-JSON error body; fall back to status text
    }
    throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as TournamentResponse;
  return body.tournament;
}

/**
 * Load the published photo gallery for a shared tournament (unauthenticated).
 * Returns the READY photos only; resolves to [] on 404 (no such tournament) so a
 * stale link degrades gracefully. Throws on other network/server errors.
 */
export async function fetchPublicPhotos(id: string): Promise<PublicPhoto[]> {
  const res = await fetch(
    `${BASE_URL}/public/tournaments/${encodeURIComponent(id)}/photos`,
    { headers: { Accept: 'application/json' } },
  );
  if (res.status === 404) return [];
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      // non-JSON error body; fall back to status text
    }
    throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as { photos: PublicPhoto[] };
  return body.photos;
}

/**
 * Load a public athlete/coach profile by its opaque share token
 * (unauthenticated). Resolves to null when the token is unknown OR the profile
 * is not public (HTTP 404) — a stale or private link degrades gracefully.
 * Throws on network or unexpected server errors so the caller can retry.
 */
export async function fetchPublicProfile(
  token: string,
): Promise<PublicAthleteProfile | null> {
  const res = await fetch(
    `${BASE_URL}/public/profiles/${encodeURIComponent(token)}`,
    { headers: { Accept: 'application/json' } },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      // non-JSON error body; fall back to status text
    }
    throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as { profile: PublicAthleteProfile };
  return body.profile;
}

/**
 * Load a public sport club home page by its opaque share token
 * (unauthenticated). Resolves to null when the token is unknown OR the club's
 * home page is not public (HTTP 404) — a stale or private link degrades
 * gracefully. Throws on network or unexpected server errors so the caller can
 * retry.
 */
export async function fetchPublicClubHome(
  token: string,
): Promise<PublicClubHome | null> {
  const res = await fetch(`${BASE_URL}/public/clubs/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    let detail = '';
    try {
      detail = ((await res.json()) as { error?: string }).error ?? '';
    } catch {
      // non-JSON error body; fall back to status text
    }
    throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as { home: PublicClubHome };
  return body.home;
}

/** Per-field validation messages returned by a rejected registration. */
export interface RegistrationFieldErrors {
  fullName?: string;
  dateOfBirth?: string;
  mobile?: string;
}

/**
 * Thrown when a registration submission is rejected. `fields` carries per-field
 * messages for a 400 validation failure; it is empty for other failures (e.g.
 * registration closed), where only `message` is meaningful.
 */
export class RegistrationSubmitError extends Error {
  readonly fields: RegistrationFieldErrors;

  constructor(message: string, fields: RegistrationFieldErrors = {}) {
    super(message);
    this.name = 'RegistrationSubmitError';
    this.fields = fields;
  }
}

/**
 * Acknowledgement that a sign-up was stored and a one-time passcode is on its
 * way. Carries only the masked destination (so the form can say where the code
 * went) and the code's expiry — never any personal data.
 */
export interface OtpChallengeIssued {
  registrationId: string;
  maskedMobile: string;
  expiresAt: number;
}

interface ChallengeResponse {
  challenge: OtpChallengeIssued;
}

/**
 * Submit a public sign-up for a tournament via the unauthenticated public
 * endpoint. On success the server stores the sign-up and sends an OTP to the
 * mobile number; this resolves to the delivery acknowledgement. Throws
 * `RegistrationSubmitError` (with field messages for validation failures) on a
 * 4xx, or a plain Error on a network/unexpected failure. Complete the sign-up
 * with `verifyOtp`.
 */
export async function submitRegistration(
  id: string,
  input: RegistrationInput,
): Promise<OtpChallengeIssued> {
  const res = await fetch(
    `${BASE_URL}/public/tournaments/${encodeURIComponent(id)}/register`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (res.ok) {
    const body = (await res.json()) as ChallengeResponse;
    return body.challenge;
  }

  let detail = '';
  let fields: RegistrationFieldErrors = {};
  try {
    const errorBody = (await res.json()) as {
      error?: string;
      fields?: RegistrationFieldErrors;
    };
    detail = errorBody.error ?? '';
    fields = errorBody.fields ?? {};
  } catch {
    // non-JSON error body; fall back to status text
  }
  if (res.status >= 400 && res.status < 500) {
    throw new RegistrationSubmitError(
      detail || 'Registration could not be completed.',
      fields,
    );
  }
  throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
}

/** Machine-readable reason an OTP step failed (see the server's `OtpError`). */
export type OtpErrorCode =
  | 'NOT_FOUND'
  | 'CLOSED'
  | 'ALREADY_VERIFIED'
  | 'EXPIRED'
  | 'TOO_MANY_ATTEMPTS'
  | 'INVALID_CODE'
  | 'UNKNOWN';

/** Thrown when verifying or resending an OTP fails; `code` drives the UI copy. */
export class OtpError extends Error {
  readonly code: OtpErrorCode;

  constructor(message: string, code: OtpErrorCode) {
    super(message);
    this.name = 'OtpError';
    this.code = code;
  }
}

async function readOtpError(res: Response): Promise<OtpError> {
  let detail = '';
  let code: OtpErrorCode = 'UNKNOWN';
  try {
    const body = (await res.json()) as { error?: string; code?: OtpErrorCode };
    detail = body.error ?? '';
    if (body.code) code = body.code;
  } catch {
    // non-JSON error body; fall back to status text
  }
  return new OtpError(
    detail || `Request failed: ${res.status} ${res.statusText}`,
    code,
  );
}

/**
 * Confirm the passcode for a pending sign-up. Resolves on success; throws
 * `OtpError` (with a `code` such as `INVALID_CODE`, `EXPIRED` or
 * `TOO_MANY_ATTEMPTS`) on any failure so the form can show the right message.
 */
export async function verifyOtp(
  id: string,
  registrationId: string,
  code: string,
): Promise<void> {
  const res = await fetch(
    `${BASE_URL}/public/tournaments/${encodeURIComponent(id)}/register/verify`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ registrationId, code }),
    },
  );
  if (!res.ok) throw await readOtpError(res);
}

/**
 * Re-send a fresh passcode for a pending sign-up. Resolves to the new delivery
 * acknowledgement; throws `OtpError` on failure.
 */
export async function resendOtp(
  id: string,
  registrationId: string,
): Promise<OtpChallengeIssued> {
  const res = await fetch(
    `${BASE_URL}/public/tournaments/${encodeURIComponent(id)}/register/resend`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ registrationId }),
    },
  );
  if (!res.ok) throw await readOtpError(res);
  const body = (await res.json()) as ChallengeResponse;
  return body.challenge;
}

// --- Company sign-up wizard (see `features/company-registration.md`) ------------

/**
 * Machine-readable reason a sign-up step failed (see the server's
 * `CompanyRegistrationError`). Drives which message the wizard shows.
 */
export type SignupErrorCode =
  | 'NOT_FOUND'
  | 'INVALID'
  | 'INVALID_CODE'
  | 'EMAIL_TAKEN'
  | 'STATE'
  | 'LIMIT'
  | 'CODE_EXPIRED'
  | 'TOO_MANY_ATTEMPTS'
  | 'NOT_CONFIGURED'
  | 'DELIVERY_FAILED'
  | 'UNKNOWN';

/**
 * Thrown when a sign-up step is rejected. `fields` carries per-field messages
 * for a validation failure; `code` drives the wizard's copy otherwise.
 */
export class SignupError extends Error {
  readonly code: SignupErrorCode;
  readonly fields: Record<string, string>;

  constructor(message: string, code: SignupErrorCode, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'SignupError';
    this.code = code;
    this.fields = fields;
  }
}

interface SignupResponse {
  registration: CompanyRegistrationView;
}

/** Shared fetch for the sign-up endpoints: returns the refreshed sign-up view. */
async function signupRequest(
  path: string,
  init?: RequestInit,
): Promise<CompanyRegistrationView> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });
  if (res.ok) {
    return ((await res.json()) as SignupResponse).registration;
  }
  let detail = '';
  let code: SignupErrorCode = 'UNKNOWN';
  let fields: Record<string, string> = {};
  try {
    const body = (await res.json()) as {
      error?: string;
      code?: SignupErrorCode;
      fields?: Record<string, string>;
    };
    detail = body.error ?? '';
    if (body.code) code = body.code;
    fields = body.fields ?? {};
  } catch {
    // non-JSON error body; fall back to status text
  }
  throw new SignupError(
    detail || `Request failed: ${res.status} ${res.statusText}`,
    code,
    fields,
  );
}

function signupJson(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

/**
 * Load the location dictionary through the unauthenticated endpoint, for the
 * wizard's location picker (the visitor has no account yet).
 */
export async function fetchPublicLocations(): Promise<Location[]> {
  const res = await fetch(`${BASE_URL}/public/locations`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Request failed: ${res.status} ${res.statusText}`);
  }
  return ((await res.json()) as { locations: Location[] }).locations;
}

/**
 * Start a club sign-up: stage the admin account and trigger the verification
 * passcode email. Resolves to the staged sign-up (its `id` is the capability
 * for every later step). Throws `SignupError` — with per-field messages for
 * validation failures, `EMAIL_TAKEN` for an address that already has an
 * account.
 */
export function startSignup(input: {
  email: string;
  name: string;
  password: string;
  language: string;
}): Promise<CompanyRegistrationView> {
  return signupRequest('/public/signup', signupJson(input));
}

/** Reload a sign-up so a reopened wizard can resume where it left off. */
export function fetchSignup(id: string): Promise<CompanyRegistrationView> {
  return signupRequest(`/public/signup/${encodeURIComponent(id)}`);
}

/** Confirm the emailed passcode, opening the wizard's company steps. */
export function verifySignupEmail(
  id: string,
  code: string,
): Promise<CompanyRegistrationView> {
  return signupRequest(
    `/public/signup/${encodeURIComponent(id)}/verify-email`,
    signupJson({ code }),
  );
}

/** Ask for a fresh passcode (replaces the previous one). */
export function resendSignupCode(id: string): Promise<CompanyRegistrationView> {
  return signupRequest(`/public/signup/${encodeURIComponent(id)}/resend-code`, {
    method: 'POST',
  });
}

/** Stage the club's name and location. */
export function saveSignupDetails(
  id: string,
  input: { companyName: string; locationId: number },
): Promise<CompanyRegistrationView> {
  return signupRequest(
    `/public/signup/${encodeURIComponent(id)}/details`,
    signupJson(input),
  );
}

/** Stage the club's (optional, markdown) description. */
export function saveSignupDescription(
  id: string,
  description: string,
): Promise<CompanyRegistrationView> {
  return signupRequest(
    `/public/signup/${encodeURIComponent(id)}/description`,
    signupJson({ description }),
  );
}

/** Upload the club logo as raw image bytes; the staged URL rides the view. */
export function uploadSignupLogo(
  id: string,
  blob: Blob,
): Promise<CompanyRegistrationView> {
  return signupRequest(`/public/signup/${encodeURIComponent(id)}/logo`, {
    method: 'POST',
    headers: { 'Content-Type': blob.type || 'application/octet-stream' },
    body: blob,
  });
}

/** Upload one club photo as raw image bytes (processed synchronously). */
export function uploadSignupPhoto(
  id: string,
  blob: Blob,
): Promise<CompanyRegistrationView> {
  return signupRequest(`/public/signup/${encodeURIComponent(id)}/photos`, {
    method: 'POST',
    headers: { 'Content-Type': blob.type || 'application/octet-stream' },
    body: blob,
  });
}

/** Drop a staged club photo. */
export function deleteSignupPhoto(
  id: string,
  photoId: string,
): Promise<CompanyRegistrationView> {
  return signupRequest(
    `/public/signup/${encodeURIComponent(id)}/photos/${encodeURIComponent(photoId)}/delete`,
    { method: 'POST' },
  );
}

/**
 * Complete the wizard: the server creates the (unverified) company and its
 * Company Admin account, and emails "we are checking your company details".
 */
export function finalizeSignup(id: string): Promise<CompanyRegistrationView> {
  return signupRequest(`/public/signup/${encodeURIComponent(id)}/finalize`, {
    method: 'POST',
  });
}
